import React, {Fragment, useContext} from 'react';
import {DataTablePage} from '@common/datatable/page/data-table-page';
import {Trans} from '@ui/i18n/trans';
import {DataTableEmptyStateMessage} from '@common/datatable/page/data-table-emty-state-message';
import movieNightImage from '@app/admin/titles/movie-night.svg';
import {Link} from 'react-router-dom';
import {Lien, LienResport} from '@app/titles/models/lien';
import {ColumnConfig} from '@common/datatable/column-config';
import {ChipList} from '@ui/forms/input-field/chip-field/chip-list';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {FormattedNumber} from '@ui/i18n/formatted-number';
import {EditIcon} from '@ui/icons/material/Edit';
import {EnumLinkQualityChip} from '@ui/i18n/enum-quality';
import {Tooltip} from '@ui/tooltip/tooltip';
import {IconButton} from '@ui/buttons/icon-button';
import {useAuth} from '@common/auth/use-auth';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {NfoDialog} from '@app/liens/nfo-dialog';
import {InfoIcon} from '@ui/icons/material/Info';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {Button} from '@ui/buttons/button';
import {TitlePoster} from '@app/titles/title-poster/title-poster';
import {TitleLink} from '@app/titles/title-link';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import {FlagIcon} from '@ui/icons/material/Flag';
import {DeleteSelectedItemsAction} from '@common/datatable/page/delete-selected-items-action';
import {useTrans} from '@ui/i18n/use-trans';
import {Table, TableBodyProps} from '@common/ui/tables/table';
import {TableContext} from '@common/ui/tables/table-context';
import {TableRow} from '@common/ui/tables/table-row';
import {DeleteIcon} from '@ui/icons/material/Delete';
import {DeleteReportDialog} from '@app/reports/requests/dialog-reports';
import {FormattedDate} from '@ui/i18n/formatted-date';

export interface CreateReportPayload {
  report: string;
}

const columnConfig: ColumnConfig<LienResport>[] = [
  {
    key: 'username',
    header: () => <Trans message="Username" />,
    visibleInMode: 'all',
    body: report => (
      <div className="flex items-center gap-12">
        <div className="min-w-0 overflow-hidden">{report.user.username}</div>
      </div>
    ),
  },
  {
    key: 'report',
    header: () => <Trans message="Report" />,
    body: report => (
      <div className="flex items-center gap-12">
        <div className="min-w-0 overflow-hidden">{report.report}</div>
      </div>
    ),
  },
];

function ReportTableBody({renderRowAs}: TableBodyProps) {
  const {data} = useContext(TableContext);
  return (
    <Fragment>
      {data.map((item, rowIndex) => (
        <TableRow
          item={item}
          index={rowIndex}
          key={item.id}
          renderAs={renderRowAs}
        />
      ))}
    </Fragment>
  );
}

export function LiensListReportedDatatablePage() {
  const {hasPermission} = useAuth();
  const {trans} = useTrans();

  const actions: ColumnConfig<Lien>[] = [
    {
      key: 'actions',
      header: () => <Trans message="Actions" />,
      hideHeader: true,
      minWidth: 'min-w-100',
      visibleInMode: 'all',
      align: 'end',
      body: lien => (
        <div className={'inline-flex'}>
          <DialogTrigger type="modal">
            <IconButton size="md" className="text-muted">
              <FlagIcon />
            </IconButton>
            <Dialog size="md">
              <DialogHeader>
                <Trans message="Report message" />
              </DialogHeader>
              <DialogBody>
                {lien.reports.length > 0 && (
                  <Table
                    enableSelection={false}
                    columns={columnConfig}
                    data={lien.reports}
                    cellHeight="h-54"
                    tableBody={<ReportTableBody />}
                  />
                )}
              </DialogBody>
            </Dialog>
          </DialogTrigger>

          <DialogTrigger type="modal">
            <IconButton size="md" className="text-muted">
              <InfoIcon />
            </IconButton>
            <NfoDialog id={lien.id} />
          </DialogTrigger>

          {hasPermission('liens.update') && (
            <Link
              to={`/admin/liens/${lien.title_id}/${lien.id}/edit`}
              className="text-muted"
            >
              <Tooltip label={<Trans message="Edit" />}>
                <IconButton size="md">
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Link>
          )}
          <DialogTrigger type="modal">
            <IconButton size="md" className="text-muted">
              <DeleteIcon />
            </IconButton>
            <DeleteReportDialog item={lien} />
          </DialogTrigger>
        </div>
      ),
    },
  ];
  const commonColumns: ColumnConfig<Lien>[] = [
    {
      key: 'id',
      width: 'flex-3',
      allowsSorting: true,
      visibleInMode: 'all',
      header: () => <Trans message="ID" />,
      body: lien => lien.id,
    },
    {
      key: 'title',
      allowsSorting: true,
      width: 'flex-3',
      visibleInMode: 'all',
      header: () => <Trans message="Title" />,
      body: lien =>
        lien.post ? (
          <div className="flex items-center gap-12">
            <TitlePoster
              title={lien.post}
              srcSize="sm"
              size="w-32"
              showLastLink={false}
              aspect="aspect-square"
            />
            <div className="min-w-0 overflow-hidden">
              <div className="overflow-hidden overflow-ellipsis">
                <TitleLink title={lien.post} target="_blank" />
              </div>
            </div>
          </div>
        ) : null,
    },
    {
      key: 'Uploader',
      visibleInMode: 'regular',
      allowsSorting: false,
      header: () => <Trans message="Uploader" />,
      body: lien => (
        <Link to={`/get-user/${lien.id_user}`}>{lien.id_user}</Link>
      ),
    },
    {
      key: 'lien',
      width: 'flex-3',
      visibleInMode: 'all',
      header: () => <Trans message="Lien" />,
      body: lien => (
        <Fragment>
          <a href={lien.lien} target="_blank" rel="noreferrer">
            {lien.lien}
          </a>
        </Fragment>
      ),
    },
    {
      key: 'categorie',
      allowsSorting: true,
      header: () => <Trans message="Category" />,
      body: lien => lien.post?.categorie?.name,
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'qualite',
      allowsSorting: true,
      visibleInMode: 'all',
      header: () => <Trans message="Quality" />,
      body: lien => <EnumLinkQualityChip quality={lien.qual.qual} />,
    },
    {
      key: 'langues_compact',
      visibleInMode: 'all',
      header: () => <Trans message="Languages" />,
      body: lien => {
        const langues = lien?.langues_compact || [];
        const displayedLangues = langues.slice(0, 2);
        const remainingLangues = langues
          .slice(2)
          .map(lang => lang.name)
          .join(', ');

        return (
          <>
            <ChipList radius="rounded" className="z-50" size="xs">
              <>
                {displayedLangues?.map(lang => (
                  <Chip key={lang.id} selectable>
                    <Trans message={lang.name} />
                  </Chip>
                ))}
                {langues?.length > 2 && (
                  <DialogTrigger type="modal">
                    <Button variant="link">
                      +{lien?.langues_compact.length} <Trans message={'more'} />
                    </Button>
                    <PreviewDialog remainingLangues={remainingLangues} />
                  </DialogTrigger>
                )}
              </>
            </ChipList>
          </>
        );
      },
    },
    {
      key: 'subs_compact',
      visibleInMode: 'all',
      header: () => <Trans message="Subtitles" />,
      body: lien => {
        const langues = lien?.subs_compact || [];
        const displayedLangues = langues.slice(0, 2);
        const remainingLangues = langues
          .slice(2)
          .map(lang => lang.name)
          .join(', ');

        return (
          <>
            <ChipList radius="rounded" className="z-50" size="xs">
              <>
                {displayedLangues?.map(lang => (
                  <Chip key={lang.id} selectable>
                    <Trans message={lang.name} />
                  </Chip>
                ))}
                {langues?.length > 2 && (
                  <DialogTrigger type="modal">
                    <Button variant="link">
                      +{lien?.subs_compact.length} <Trans message={'more'} />
                    </Button>
                    <PreviewDialog remainingLangues={remainingLangues} />
                  </DialogTrigger>
                )}
              </>
            </ChipList>
          </>
        );
      },
    },
    {
      key: 'saison',
      allowsSorting: true,
      minWidth: 'min-w-100',
      visibleInMode: 'all',
      header: () => <Trans message="Season" />,
      body: lien => <FormattedNumber value={lien.saison} />,
    },
    {
      key: 'episode',
      allowsSorting: true,
      minWidth: 'min-w-100',
      visibleInMode: 'all',
      header: () => <Trans message="Episode" />,
      body: lien =>
        lien.full_saison ? (
          <Trans message={'Full season'} />
        ) : (
          <FormattedNumber value={lien.episode} />
        ),
    },
    {
      key: 'created_at',
      allowsSorting: true,
      header: () => <Trans message="Reported at" />,
      body: lien => {
        const lastReport = lien.reports?.at(-1); // Récupère le dernier élément du tableau
        return lastReport ? (
          <FormattedDate date={lastReport.created_at} />
        ) : (
          '-'
        );
      },
    },
  ];

  const LiensListDatatableColumnsSerie: ColumnConfig<Lien>[] = [
    ...commonColumns,
    ...actions,
  ];

  return (
    <DataTablePage
      title={<Trans message="Reported links" />}
      endpoint={'liens'}
      enableSearch={true}
      pinnedFilters={['id_host', 'qualite', 'langues']}
      queryParams={{
        loader: 'adminliens',
        reported: 1,
      }}
      columns={LiensListDatatableColumnsSerie}
      selectedActions={
        hasPermission('liens.delete') && <DeleteSelectedItemsAction />
      }
      emptyStateMessage={
        <DataTableEmptyStateMessage
          image={movieNightImage}
          title={<Trans message="No links have been created yet" />}
          filteringTitle={<Trans message="No matching links" />}
        />
      }
    />
  );
}

function PreviewDialog({remainingLangues}: {remainingLangues: string}) {
  return (
    <Dialog size="sm" className="flex flex-col">
      <DialogBody>{remainingLangues}</DialogBody>
    </Dialog>
  );
}
